<template>
	<div
		id="containerDiv"
		class="pronto-container"
		:class="{ 'thread-open': activeThread }"
		@keyup.esc="closeActions()"
	>
		<AccessibleManager ref="accessibleManager" :sectionOrder="sectionManager">
			<CloudLoader animate v-if="showLoadingScreen">
				<div
					class="loading-too-long-container"
					v-if="elapsedLoadingTime > FIRST_LOADING_CHECK_MS"
				>
					<div v-t="'chat_loading_too_long'" />
					<div
						v-if="elapsedLoadingTime > MAX_LOADING_CHECK_MS"
						v-t="'chat_loading_too_long_refresh'"
					/>
				</div>
			</CloudLoader>

			<AnnouncementBanner
				v-if="showAnnouncementsBanner"
				@detail="detailAnnouncement = $event"
			/>

			<AnnouncementModal
				v-if="detailAnnouncement"
				:announcement="detailAnnouncement"
				@cancel="detailAnnouncement = null"
			/>

			<AccessibleSection
				name="search-bar"
				rootSelector=".search-bar-container > .search-bar"
				:tabOrder="[
					{
						css: '.search-input-container',
						Enter: (vm) => {
							vm.$el.focus()
						},
					},
				]"
				tabindex="-1"
			>
				<SearchBar
					v-if="showSearchBar && !showLoadingScreen"
					:queryParams="$route.query"
				/>
			</AccessibleSection>

			<div
				v-if="!showLoadingScreen"
				class="app-layout"
				:class="{
					announcement: showAnnouncementsBanner,
					relative: $route.name !== 'announcements',
					condensed: condensedSize,
					search: showSearchBar,
					'thread-open': activeThread,
					'has-topbar':
						condensedSize &&
						bubblesLoaded &&
						$route.name !== 'mainsettings' &&
						$route.path !== '/external_launch',
				}"
				:style="appLayoutStyle"
			>
				<AccessibleSection
					v-if="
						condensedSize &&
						bubblesLoaded &&
						$route.name !== 'mainsettings' &&
						$route.path !== '/external_launch'
					"
					name="condensed-topbar"
					class="condensed-topbar-section"
					:tabOrder="[
						{ css: '.topbar-logo-link' },
						{
							css: '.topbar-action-btn',
							clickOnEnter: true,
							all: '.topbar-action-btn',
						},
					]"
				>
					<CondensedTopBar />
				</AccessibleSection>

				<Sidebar
					:condensedSize="condensedSize"
					:hamburgerActive="hamburgerActive"
				/>

				<div
					v-if="condensedSize"
					class="sidebar-overlay"
					:class="{ active: hamburgerActive }"
					@click="clickHamburger"
				/>

				<div
					v-if="activeThread && threadOverlayMode"
					class="thread-overlay"
					@click="closeThread"
				/>

				<div v-if="isChatRoute" v-show="!(hamburgerActive && condensedSize)">
					<ThreadViewHeader
						v-if="currentChat?.id && currentChat.is_thread"
						:chatId="currentChat.id"
						showDrawerToggle
						fullView
					/>
					<MessageListHeader
						v-else-if="currentChat?.id"
						:chatId="currentChat.id"
						:inAppUnreads="allInAppUnreads"
					/>
				</div>

				<AccessibleSection
					name="meeting-banner"
					:tabOrder="[{ css: '.join-text', clickOnEnter: true }]"
					v-show="isChatRoute && !currentChat?.is_thread"
				>
					<MeetingBanner
						:hide="hamburgerActive"
						@showMeetingBanner="showMeetingBanner = $event"
					/>
				</AccessibleSection>

				<router-view v-slot="{ Component }">
					<keep-alive include="Search">
						<component
							:is="Component"
							:key="$route.params.id"
							ref="routerview"
							@loaded="chatLoaded"
							:groupSettingsOpen="showGroupSettings || null"
							:class="{
								embedded: store.config.embedded,
							}"
						/>
					</keep-alive>
				</router-view>

				<ResizeHandle
					v-if="activeThread && isChatRoute && !threadOverlayMode"
					:currentWidth="store.threadPanelWidth"
					:maxWidth="maxThreadWidth"
					@resize="onThreadResize"
					@resize-end="onThreadResizeEnd"
				/>

				<ThreadView
					v-if="activeThread && isChatRoute"
					:thread="activeThread"
					@close="closeThread"
					@thread-created="onThreadCreated"
				/>
			</div>

			<div
				v-if="showError"
				v-bind:class="{
					'error-wrapper': !store.config.embedded,
					'error-wrapper-embedded': store.config.embedded,
				}"
			>
				<div class="error-alert">
					<span>{{ errorText }}</span>
					<input
						type="image"
						src="/images/common/white-icon-close.png"
						@click="showError = false"
						:alt="$t('common_button_close')"
					/>
				</div>
			</div>

			<CreateDM
				v-if="createDm"
				@close="createDm = false"
				@openGroup="openGroupModal"
			/>

			<CreateGroup
				v-if="createGroup"
				@close="createGroup = false"
				:initialUsers="createGroupUsers"
			/>

			<InviteCodeModal
				v-if="enterGroupInvite"
				@code="onInviteCode"
				@cancel="enterGroupInvite = false"
			/>

			<TaskDetail
				v-if="showNewTask"
				@close="closeAllTaskScreens"
				v-bind:taskdata="taskData"
			/>

			<GroupInviteLink
				v-if="showGroupInvite"
				:inviteCode="groupInviteCode"
				@cancel="groupInviteCode = null"
				@success="groupInviteCode = null"
			/>

			<SimpleModal
				v-if="showLastOwnerPopup"
				@close="showLastOwnerPopup = false"
			>
				<div style="max-width: 400px; min-height: 300px; min-width: 400px">
					<div>
						<div class="new-team-wrapper">
							<div class="upload-title">😢</div>
							<span
								class="leave-modal-text"
								style="font-size: 15px; width: 400px; padding: 0 25px"
								>{{ $t('apierrors_LAST_OWNER_ERROR') }}</span
							>
						</div>
						<div class="button-group">
							<button
								class="btn-active-lg"
								style="min-width: 100px"
								@click="showLastOwnerPopup = false"
							>
								{{ $t('common_button_ok') }}
							</button>
						</div>
					</div>
				</div>
			</SimpleModal>

			<MediaModal />

			<ForwardComposer
				v-if="showForwardCard"
				:messageId="forwardMessageId"
				:fullscreen="!!store.config.embedded"
				@close="showForwardCard = false"
			/>

			<OnboardingModal v-if="showOnboarding" @close="showOnboarding = false" />

			<div v-if="showFail" class="error-wrapper">
				<div class="error-alert">
					<span>{{ alertText }}</span>
					<input
						type="image"
						src="/images/common/white-icon-close.png"
						@click="showFail = false"
					/>
				</div>
			</div>
		</AccessibleManager>
	</div>
</template>

<script>
import store, {
	getUser,
	getUsers,
	setUser,
	setMembership,
	updateUser,
	updateUserPresence,
	updateMessage,
	getMessage,
	deleteMessage,
	setMessage,
	getMessageIdsForTaskId,
	getMyUserId,
	isSafari,
	setChat,
	deleteChat,
	setStats,
	setStat,
	getChats,
	getRecentChats,
	getChatsWithNoCategory,
	getDMChats,
	diffChatsInCache,
	setCategory,
	getStat,
	getCurrentChat,
	getCategory,
	getActiveCategories,
	deleteUpload,
	getUsersForBubble,
	sortUsersByFullname,
	getChat,
	getThreadsForChat,
	updateChat,
	getEffectiveMentions,
} from '@/store'

import { get, isCanceled, post } from '@/helpers/api'
import { pushNotification, storeNotification } from '@/helpers/pushNotification'
import { length, substr } from 'stringz'

import CreateDM from '@/components/CreateDM/CreateDM'
import CreateGroup from '@/components/CreateGroup/CreateGroup'
import { SimpleModal, MediaModal, CloudLoader, UnreadBadge } from '../common'
import TaskDetail from '../TaskDetail'
import GroupInviteLink from '@/components/GroupSettings/GroupInviteLink'
import InviteCodeModal from '@/components/Invite/CodeModal'
import { AnnouncementBanner, AnnouncementModal } from '../Announcements'
import MeetingBanner from '../Meeting/MeetingBanner'
import SearchBar from '../Search/SearchBar'
import ForwardComposer from '@/components/Message/ForwardComposer'
import Sidebar from './Sidebar'
import CondensedTopBar from './CondensedTopBar'
import OnboardingModal from './OnboardingModal'
import {
	AccessibleSection,
	AccessibleManager,
} from '@/components/common/Accessible'
import moment from 'moment'
import debounce from 'debounce'
import {
	initPusher,
	bindAppEvents,
	bindChannelEventWithChunking,
	getPusherInstance,
	getPresenceChannel,
	subscribeToPresence,
	unsubscribeToPresence,
	subscribeToPrivateUser,
} from '@/helpers/pusher'
import {
	resubscribeThreadPreview,
	getActiveThreadPreviewIds,
} from '@/helpers/threadPreviewPusher'
import {
	calculateLastActiveText,
	sStorage,
	storage,
	trackEvent,
	openPopout,
} from '@/helpers/utils'
import { timedLogger } from '@/helpers/log'
import { getRequiredOnboardingSteps } from '@/helpers/startup'
import {
	updateDevice,
	enablePushNotifications,
	getPushToken,
} from '@/api/device'
import {
	redirectToLastActiveGroup,
	getDeviceInfo,
	redirectToRecentsOrChat,
} from '@/helperMethods'
import { fetchMyUserInfo } from '@/api/user'
import { fetchAllUnreadAnnouncements } from '@/api/announcement'
import { setAnnouncement, deleteAnnouncement } from '@/store/announcements'
import {
	createDM,
	fetchMyMemberships,
	fetchMyMembershipForChat,
} from '@/api/chat'
import i18n from '@/i18n'
import { MAX_UNREADS, UNREAD_LABEL } from '@/helpers/constants'
import { chatCan, CREATE_VIDEOSESSION } from '@/helpers/permissions'
import { setDocTitleFromCurrentRoute } from '@/router'
import { orgFlag, SEARCH } from '@/helpers/flags'
import MessageListHeader from '@/components/Chat/MessageListHeader'
import ThreadViewHeader from '@/components/Chat/ThreadViewHeader'
import ThreadView from '@/components/Chat/ThreadView'
import ResizeHandle from '@/components/Home/ResizeHandle'
import { postFromIframe, getImageFilesFromClipboard } from '@/helpers/utils'

const FIFTEEN_MINS = 900000
const FIRST_LOADING_INTERVAL_MS = 5000
const FIRST_LOADING_CHECK_MS = 10000
const MAX_LOADING_CHECK_MS = 20000
const DRAWER_TRANSITION_MS = 300

export default {
	components: {
		CreateDM,
		CreateGroup,
		SimpleModal,
		TaskDetail,
		ForwardComposer,
		AnnouncementBanner,
		AnnouncementModal,
		MeetingBanner,
		SearchBar,
		MediaModal,
		CloudLoader,
		GroupInviteLink,
		InviteCodeModal,
		AccessibleManager,
		AccessibleSection,
		UnreadBadge,
		Sidebar,
		CondensedTopBar,
		OnboardingModal,
		MessageListHeader,
		ThreadViewHeader,
		ThreadView,
		ResizeHandle,
	},

	data() {
		return {
			store,
			allUnreadAnnouncementsLoaded: false,
			lastUnreadAnnouncementId: null,
			detailAnnouncement: null,
			groupInviteCode: null,
			hasBubbles: false,
			bubblesLoaded: false,
			condensedSize: false,
			notificationsEnabled: false,
			hamburgerActive: false,
			hamburgerDisabled: false,
			createDm: false,
			createGroup: false,
			enterGroupInvite: false,
			showNewTask: false,
			tasksActive: false,
			showError: false,
			errorText: '',
			forwardMessageId: null,
			showForwardCard: false,
			taskData: '',
			presenceTimeout: null,
			showGroupSettings: false,
			showLastOwnerPopup: false,
			showFail: false,
			alertText: null,
			windowWidth: null,
			windowHeight: null,
			externalGroup: null,
			showSearchBar: false,
			i18nLoaded: i18n.loaded || false,
			categories: getActiveCategories(),
			groupChats: getChatsWithNoCategory(),
			dmChats: getDMChats(),
			allUnreads: 0,
			allExternalUnreads: 0,
			allInAppUnreads: 0,
			currentChat: getCurrentChat(),
			hasRecentChats: getRecentChats().length,
			hasPreviouslyConnected: false,
			showMeetingBanner: false,
			showOnboarding: false,
			elapsedLoadingTime: 0,
			i18n,
			FIRST_LOADING_CHECK_MS,
			MAX_LOADING_CHECK_MS,
			activeThread: null,
		}
	},

	watch: {
		'$route.params.id': function (newId, oldId) {
			if (this.activeThread && newId !== oldId) {
				this.activeThread = null
			}
		},

		'$route.path': {
			handler(path) {
				this.hamburgerActive = false

				if (path == '/tasks') {
					this.tasksActive = true
				} else {
					this.tasksActive = false
				}

				this.showGroupSettings = false
			},
			deep: true,
		},

		'this.store.tasks.stats.tasksDue': {
			handler(val) {
				this.allExternalUnreads = this.calcAllExternalUnreads()
				this.allInAppUnreads = this.calcAllInAppUnreads()
				this.allUnreads = this.calcAllUnreads()
			},
			deep: true,
		},

		windowWidth: function (val) {
			this.accessibilityMode = false
			if (val <= 800) {
				this.condensedSize = true
				EventBus.$emit('condensedSize', true)
			} else {
				this.condensedSize = false
				EventBus.$emit('condensedSize', false)
			}
		},

		'store.appVisible': {
			handler() {
				this.store.today = moment().utc().format('YYYY-MM-DD HH:mm:ss')
			},
			deep: true,
		},

		currentChat: function (val, oldVal) {
			this.updateTabBadge()

			if (val && this.store.config.wrapper) {
				let data = {
					type: 'groupUpdate',
					title: getCurrentChat().displayname,
					category: this.categoryTitle,
					unread: this.allExternalUnreads,
				}
				this.postFromIframe(data)
			}
		},

		'store.currentChatId': {
			handler() {
				this.currentChat = getCurrentChat()
			},
			deep: true,
		},

		allUnreads: function () {
			if (this.store.config.wrapper) {
				let data = { type: 'unreadUpdate', unread: this.allUnreads }
				this.postFromIframe(data)
			}
		},

		allExternalUnreads: function (val, oldVal) {
			this.updateTabBadge()
		},
	},

	computed: {
		sectionManager() {
			const threadSections =
				this.activeThread && this.isChatRoute
					? ['thread_chat-header', 'thread_chat-messages', 'thread_chat-input']
					: []
			if (this.condensedSize) {
				return this.hamburgerActive
					? ['condensed-topbar', 'sidebar-header', 'sidebar-main']
					: [
							'condensed-topbar',
							'chat-header',
							'chat-messages',
							'chat-input',
							...threadSections,
					  ]
			}
			const basics = ['sidebar-header', 'sidebar-main', 'fab']
			if (this.showAnnouncementsBanner) {
				basics.push('announcementbanner')
			}
			if (orgFlag(SEARCH)) {
				basics.push('search-bar')
			}
			if (this.$route.name === 'announcements') {
				return [...basics, 'announcements']
			}
			if (this.$route.name === 'tasks') {
				return [...basics, 'tasks']
			}
			if (this.$route.name === 'hiddengroups') {
				return [...basics, 'hiddengroups']
			}
			if (this.showMeetingBanner) {
				return [
					...basics,
					'chat-header',
					'meeting-banner',
					'chat-messages',
					'chat-input',
					...threadSections,
				]
			}
			return [
				...basics,
				'chat-header',
				'chat-messages',
				'chat-input',
				...threadSections,
			]
		},

		isChatRoute() {
			return ['chat', 'chatthread'].includes(this.$route.name)
		},

		threadOverlayMode() {
			return this.windowWidth <= 1100
		},

		maxThreadWidth() {
			return this.windowWidth - 280 - 440
		},

		appLayoutStyle() {
			if (!this.activeThread || this.threadOverlayMode) return {}
			return { '--thread-width': `${this.store.threadPanelWidth}px` }
		},

		showAnnouncementsBanner() {
			if (this.showLoadingScreen) return false
			return (
				this.store.showAnnouncementsBanner ||
				this.store.askForNotificationPermission
			)
		},

		unclaimedTaskCount() {
			return getStat(store.currentChatId).unclaimed_task_count
		},

		active() {
			if (this.currentChat.isdm) {
				return getUser(this.currentChat.dmpartner_id) || true
			} else {
				return true
			}
		},

		showGroupInvite() {
			return !!this.groupInviteCode
		},

		showLoadingScreen() {
			return !this.bubblesLoaded || !this.i18nLoaded || this.hasNewDM
		},

		hasUnclaimed() {
			return getStat(this.currentChat.id)?.unclaimed_task_count
		},

		currentUrl() {
			return window.location.href.split('?')[0]
		},

		categoryTitle() {
			if (
				this.currentChat.category_id &&
				getCategory(this.currentChat.category_id)
			) {
				return getCategory(this.currentChat.category_id).displayname
			} else {
				return ''
			}
		},

		memberUserIds() {
			if (!this.currentChat.isMultiDM) return []
			const myId = getMyUserId()
			return getUsersForBubble(this.currentChat.id)
				.toSorted(sortUsersByFullname)
				.map((u) => u.id)
				.filter((id) => id !== myId)
		},

		showDMActivity() {
			return !!this.dmActivity
		},

		dmActivity() {
			const chat = getCurrentChat()
			if (!chat || !chat.isdm || !chat.dmpartner_id) {
				// if no chat data, show nothing
				return null
			}
			return calculateLastActiveText(getUser(chat.dmpartner_id))
		},

		hasNewDM() {
			return this.$route.query.dmuser || false
		},

		meetingsDisabled() {
			return (
				chatCan(getCurrentChat(), CREATE_VIDEOSESSION) || this.currentChatLive
			)
		},

		currentChatLive() {
			const videosession = store.livestreaming.videosession
			return (
				videosession.status === 'ACTIVE' &&
				videosession.bubble_id === store.currentChatId
			)
		},
	},

	async created() {
		this.$onEventBus({
			'dormancy-refresh': async () => {
				console.debug('[dormancy] refresh: re-init pusher + reload bubbles')
				this.initPusherUser()
				try {
					await this.loadBubbleList()
					console.debug('[dormancy] refresh complete')
				} catch (e) {
					console.warn('[dormancy] refresh loadBubbleList failed', e)
				}
			},
			'chat-update': () => {
				if (!this.bubblesLoaded && store.bubbleListComplete) {
					this.bubblesLoaded = true
				}
				this.categories = getActiveCategories()
				this.groupChats = getChatsWithNoCategory()
				this.dmChats = getDMChats()
				if (store.currentChatId) {
					this.currentChat = getCurrentChat()
				}
				this.allExternalUnreads = this.calcAllExternalUnreads()
				this.allInAppUnreads = this.calcAllInAppUnreads()
				this.allUnreads = this.calcAllUnreads()
				this.hasRecentChats = getRecentChats().length
			},
			escape: () => {
				EventBus.$emit('close_context')
			},
			showLastOwnerPopup: (data) => {
				this.showLastOwnerPopup = true
			},
			hamburgerActive: (data) => {
				this.hamburgerActive = data
			},
			hamburgerDisabled: (data) => {
				this.hamburgerDisabled = data
			},
			toggleDrawer: () => {
				this.clickHamburger()
			},
			closeDrawerFromEscape: () => {
				if (this.hamburgerActive) {
					this.hamburgerActive = false
					EventBus.$emit('hamburgerActive', false)
					// Re-enable accessibility mode since we want to focus chat-header
					this.accessibilityMode = true
					// Wait for transition, then focus drawer toggle in chat-header
					setTimeout(() => {
						this.$refs.accessibleManager?.moveToSectionByName('chat-header')
					}, DRAWER_TRANSITION_MS)
				}
			},
			showFailBanner: (data) => {
				this.alertText = data.error
				this.showFail = true
			},
			openPopout: (data) => {
				this.openPopout(data)
			},
			openGroupInviteLink: (data) => {
				this.groupInviteCode = data
			},
			closeGroupSettings: () => {
				this.showGroupSettings = false
			},
			'i18n-loaded': () => {
				this.i18nLoaded = true
			},
			omniboxAction: (data) => {
				switch (data) {
					case 'create_group':
						this.openGroupModal()
						break

					case 'create_direct_message':
						this.createDirectMessage()
						break

					case 'create_task':
						this.createNewTask()
						break

					case 'send_announcement':
						this.createNewAnnouncement()
						break

					case 'enter_group_code':
						this.enterCode()
						break
				}
			},
			forwardCardOpen: (data) => {
				this.showForwardCard = data.open
				if (data.message_id) {
					this.forwardMessageId = data.message_id
				}
			},
			unknownUserAdded: debounce(() => this.fetchOrgPresence(), 5000),
			'announcement-update': () => {
				this.allExternalUnreads = this.calcAllExternalUnreads()
				this.allInAppUnreads = this.calcAllInAppUnreads()
				this.allUnreads = this.calcAllUnreads()
			},
			reloadPusherInstance: () => {
				this.initPusherUser()
			},
			'new-task': (data) => {
				this.newTask(data)
			},
			'open-thread': (thread) => {
				if (thread?.id) setChat(thread)
				const alreadyActive = this.activeThread?.id === thread?.id
				this.activeThread = thread
				if (!alreadyActive) this.syncThreadUrl(thread)
			},
			'close-thread': () => {
				this.activeThread = null
			},
		})

		this.store.windowStats.width = window.innerWidth
		this.store.windowStats.height = window.innerHeight

		this.windowWidth = window.innerWidth
		this.windowHeight = window.innerHeight

		// allow tracking api errors for these requests
		this.bubbleListError = 0
		this.bubbleInfoError = 0
		this.orgPresenceError = 0
		this.taskListError = 0
		this.unreadAnnouncementsError = 0

		window.addEventListener('resize', this.handleResize)

		//Check browser for notification permissions
		if (!('Notification' in window)) {
			this.notificationsEnabled = false
			this.store.askForNotificationPermission = false
		} else if (Notification.permission === 'granted') {
			this.notificationsEnabled = true
			this.store.askForNotificationPermission = false
			sessionStorage.removeItem('hideNotificationPrompt')
			localStorage.removeItem('hideNotificationPrompt')
		} else if (Notification.permission !== 'denied') {
			this.notificationsEnabled = false
			if (
				!this.store.config.wrapper &&
				!sessionStorage.getItem('hideNotificationPrompt') &&
				!localStorage.getItem('hideNotificationPrompt')
			) {
				this.store.askForNotificationPermission = true
			}
		} else {
			this.notificationsEnabled = false
			this.store.askForNotificationPermission = false
		}

		//update our device
		updateDevice({ device: getDeviceInfo() })

		// We're logged in now, so load pusher
		initPusher()

		this.userInfoPromise = this.loadUserInfo()
		if (this.store.tasksEnabled()) {
			this.loadTaskList()
		}

		// Get browser language
		try {
			if (this.store.auth.user.language) {
				this.store.language = this.store.auth.user.language
			} else {
				this.store.language = navigator.language.substr(0, 2)
			}
		} catch (error) {}

		// check for LMS integration based on the embedded flag
		if (this.store.config.embedded || this.store.config.wrapper) {
			this.store.lmsIntegration = true
		}

		this.$pusher.connection.bind('connected', () => {
			trackEvent('pusher_connected')
			console.debug('Pusher connected successfully...')
			this.initPusherUser()
			console.debug('pusher user initialized')
			this.loadBubbleList()
			console.debug('bubble list loaded')
			this.fetchOrgPresence()
			console.debug('org presence fetched')
			if (this.hasPreviouslyConnected) {
				this.loadTaskList()
				this.allUnreadAnnouncementsLoaded = false
				this.lastUnreadAnnouncementId = null
				this.store.unreadAnnouncements = []
				this.getUnreadAnnouncements()
			}
			this.hasPreviouslyConnected = true
		})

		if (this.store.config.wrapper && !getPushToken()) {
			await enablePushNotifications()
			this.listenForNotifications()
		}

		if (this.notificationsEnabled) {
			this.enableNotifications()
		} else {
			this.$pusher.connection.bind('connected', this.listenForNotifications)
		}

		this.pusherWatchdogInterval = setInterval(() => {
			// this is a watchdog process that forces pusher to reconnect in the event
			// that it becomes disconnected at any point. There's an issue with pusher
			// not automatically reconnecting after losing internet connection.
			const { onLine } = window.navigator
			const pusher = getPusherInstance()
			const { state } = pusher.connection
			if (this.prevPusherState && this.prevPusherState !== state) {
				console.debug(
					`Pusher connection: "${this.prevPusherState}" --> "${state}" (device online: ${onLine})`
				)
				if (state !== 'connected') {
					getUsers().forEach((u) => updateUser(u.id, { isonline: false }))
				}
			}
			this.prevPusherState = state
			if (onLine && state !== 'connected') {
				// eslint-disable-next-line no-console
				console.log(
					'Pusher is disconnected but device is online. Attempting to reconnect...'
				)
				pusher.connect()
				return
			}

			const presence = getPresenceChannel()
			if (this.presenceTimeout && !presence?.subscribed) {
				console.debug(
					'user should be active but presence channel not subscribed... 😬',
					presence
				)
			}
		}, 5000)

		this.apiWatchdogInterval = setInterval(() => {
			// check fo see if any previous api calls have failed (possibly due to network issues)
			// and retry any that may have failed
			if (this.bubbleListError > 0) {
				trackEvent('bubble_list_retry', { count: this.bubbleListError })
				console.debug('Bubble list error, retrying...', this.bubbleListError)
				this.loadBubbleList()
			}
			if (this.bubbleInfoError > 0) {
				trackEvent('bubble_info_retry', { count: this.bubbleInfoError })
				console.debug('Bubble info error, retrying...', this.bubbleInfoError)
				this.loadChatInfo()
			}
			if (this.orgPresenceError > 0) {
				trackEvent('org_presence_retry', { count: this.orgPresenceError })
				console.debug('Org presence error, retrying...', this.orgPresenceError)
				this.fetchOrgPresence()
			}
			if (this.taskListError > 0) {
				trackEvent('task_list_retry', { count: this.taskListError })
				console.debug('Task list error, retrying...', this.taskListError)
				this.loadTaskList()
			}
			if (this.unreadAnnouncementsError > 0) {
				trackEvent('unread_announcements_retry', {
					count: this.unreadAnnouncementsError,
				})
				console.debug(
					'Unread announcements error, retrying...',
					this.unreadAnnouncementsError
				)
				this.getUnreadAnnouncements()
			}
		}, 10000)

		const { course, group } = this.$route.query
		if (course) {
			this.externalCourseRedirect(Number(course))
		} else if (group) {
			this.externalGroupRedirect(Number(group))
		}

		this.listenForOrgEvents()

		window.addEventListener('message', (e) => {
			var data = e.data
			switch (data.type) {
				case 'focusEmbedded':
					if (this.id == data.group_id) {
						this.store.appVisible = true
					}
					break

				case 'inFocus':
					this.store.appVisible = true
					break

				case 'unFocused':
					this.store.appVisible = false
					break

				case 'loadNewGroupFromMessage':
					if (this.store.currentChatId !== data.group_id) {
						this.$router.push('/recents/' + data.group_id)
					}
					break
			}
		})

		if (this.$route.query.dmuser) {
			this.createNewDM()
		}
	},

	beforeUnmount() {
		window.removeEventListener('resize', this.handleResize)
		window.removeEventListener('paste', this.onUnfocusedPaste)
		document.removeEventListener('dragover', this.preventDefaultDrop)
		document.removeEventListener('drop', this.preventDefaultDrop)
		clearInterval(this.pusherWatchdogInterval)
		clearInterval(this.apiWatchdogInterval)
		clearTimeout(this._initPusherTimer)
	},

	mounted() {
		window.addEventListener('mousemove', debounce(this.setUserAsActive, 250))
		window.addEventListener('keyup', debounce(this.setUserAsActive, 250))

		// Prevent browser from opening dropped files - scoped dropzones handle actual file drops
		document.addEventListener('dragover', this.preventDefaultDrop)
		document.addEventListener('drop', this.preventDefaultDrop)

		// Fallback paste handler — routes unfocused pastes to the right input
		window.addEventListener('paste', this.onUnfocusedPaste)

		this.$onEventBus({
			closeNotifications: (bubble_id) => {
				this.closeNotificationsForBubble(bubble_id)
			},
			enableNotifications: () => {
				this.enableNotifications()
			},
		})

		if (this.$route.path === '/tasks') {
			this.tasksActive = true
		}

		this.setToday()

		setTimeout(this.devicePing, 300000)

		let data = { type: 'authenticated', host: window.location.host }
		this.postFromIframe(data)

		this.store.lastRefresh = moment().utc().format('YYYY-MM-DD HH:mm:ss')

		if (this.store.auth.user.organizations[0].announcements_enabled) {
			this.getUnreadAnnouncements()
		}

		if (this.$route.path !== '/external_launch' && window.google_tag_manager) {
			window.dataLayer = window.dataLayer || []
			window.gtag = function () {
				dataLayer.push(arguments)
			}
			window.gtag('js', new Date())
			if (
				this.store.auth.user &&
				!JSON.stringify(window.dataLayer).includes('user_id')
			) {
				const { user } = this.store.auth
				const { VITE_ANALYTICS_ID } = import.meta.env
				window.gtag('config', VITE_ANALYTICS_ID, {
					user_id: `${user.id}`,
					org_id: `${user.organizations[0].id}`,
					org_name: `${user.organizations[0].name}`,
				})

				if (this.store.canvasInstanceUrl) {
					window.gtag('event', 'integration_launch', {
						integration_name: 'Canvas',
					})
				}

				if ((this.store.integration_name = 'blackboard')) {
					window.gtag('event', 'integration_launch', {
						integration_name: 'Blackboard',
					})
				}
			}
		}

		if (
			this.store.config.wrapper ||
			this.store.config.embedded ||
			this.store.config.integration_name ||
			this.store.config.env === 'local'
		) {
			this.checkMediaTokenTTL()
		}

		if (this.$route.query.invite) {
			this.groupInviteCode = this.$route.query.invite
		}

		// Decide onboarding only after the self user.info response lands — the
		// dismissal flags live in client_meta, which every other user payload
		// omits, so deciding on a blind timer re-showed the modal on every
		// login whenever user.info took longer than the timer (or failed).
		// Keep the 3s pacing as a minimum so the modal never pops mid-load.
		const onboardingDelay = new Promise((resolve) => setTimeout(resolve, 3000))
		Promise.all([this.userInfoPromise, onboardingDelay]).then(() => {
			this.showOnboarding = getRequiredOnboardingSteps().length > 0
		})

		const loadingCheckStart = Date.now()
		const loadingCheckInterval = setInterval(() => {
			const elapsed = Date.now() - loadingCheckStart
			this.elapsedLoadingTime = elapsed
			// Check to see if the cloud loader is still showing
			console.debug(`Home - loading check at ${elapsed} ms...`)
			if (!this.showLoadingScreen) {
				console.debug('Home - all finished loading, cleaning up...')
				clearInterval(loadingCheckInterval)
				this.elapsedLoadingTime = 0
				return
			}
			if (elapsed < MAX_LOADING_CHECK_MS) return
			console.error('Home - oops still loading, showing retry... :(')
			clearInterval(loadingCheckInterval)
		}, FIRST_LOADING_INTERVAL_MS)
	},

	methods: {
		onUnfocusedPaste(e) {
			// Extract files synchronously — clipboardData may be nullified after handler returns
			const files = getImageFilesFromClipboard(e)
			if (!files.length) return
			// Defer so focused component handlers can claim the event first
			setTimeout(() => {
				if (e.__pasteHandled) return
				const channel = this.activeThread
					? 'paste-files-to-thread'
					: 'paste-files-to-chat'
				EventBus.$emit(channel, { files })
			}, 0)
		},

		closeThread() {
			this.activeThread = null
			this.syncThreadUrl(null)
			EventBus.$emit('close-thread')
		},

		onThreadCreated(thread) {
			this.activeThread = thread
			this.syncThreadUrl(thread)
		},

		syncThreadUrl(thread) {
			const { name, params } = this.$route
			if (!['chat', 'chatthread'].includes(name)) return
			if (thread?.id) {
				// Opening a thread — switch to chatthread route
				if (name !== 'chatthread' || params.threadId !== String(thread.id)) {
					this.$router.replace({
						name: 'chatthread',
						params: { ...params, threadId: thread.id },
					})
				}
			} else {
				// Closing a thread — switch back to chat route
				if (name === 'chatthread') {
					this.$router.replace({
						name: 'chat',
						params: { chat: params.chat, id: params.id },
					})
				}
			}
		},

		onThreadResize(width) {
			this.store.threadPanelWidth = width
		},
		onThreadResizeEnd() {
			storage.set('threadPanelWidth', this.store.threadPanelWidth)
		},

		onInviteCode(code) {
			this.groupInviteCode = code
			this.enterGroupInvite = false
		},

		async createNewDM() {
			try {
				const myId = getMyUserId()
				const ids = [myId]
				ids.push(parseInt(this.$route.query.dmuser))

				const chat = await createDM(ids)
				await this.$nextTick()
				this.$router.push(`/chat/${chat.id}`)
			} catch (e) {
				console.error(e)
				this.errorText = this.$t('common_api_error_save')
				this.showError = true
			}
		},

		calcAllUnreads() {
			const unreadTasks = this.store.tasks.stats.tasksDue || 0
			const unreadAnnouncments = this.store.unreadAnnouncements.length || 0
			return this.totalUnread() + unreadTasks + unreadAnnouncments
		},

		calcAllInAppUnreads() {
			const unreadTasks = this.store.tasks.stats.tasksDue || 0
			const unreadAnnouncments = this.store.unreadAnnouncements.length || 0
			return this.totalUnreadMentions() + unreadTasks + unreadAnnouncments
		},

		calcAllExternalUnreads() {
			return store.calcAllExternalUnreads()
		},

		checkMediaTokenTTL() {
			this.store.auth.ensureMediaToken()
		},

		handleResize() {
			this.store.windowStats.width = window.innerWidth
			this.store.windowStats.height = window.innerHeight

			this.windowWidth = window.innerWidth
			this.windowHeight = window.innerHeight
			if (window.innerWidth > 800) {
				EventBus.$emit('hamburgerActive', false)
			}
		},

		async clickHamburger() {
			this.hamburgerActive = !this.hamburgerActive
			EventBus.$emit('hamburgerActive', this.hamburgerActive)
			// After drawer toggle, wait for transition then move focus
			await this.$nextTick()
			const targetSection = this.hamburgerActive
				? 'sidebar-header'
				: 'chat-header'
			// Wait for CSS transition to complete before focusing
			setTimeout(() => {
				this.$refs.accessibleManager?.moveToSectionByName(targetSection)
			}, DRAWER_TRANSITION_MS)
		},

		closeActions() {
			EventBus.$emit('close_context')

			this.showGroupSettings = false
			this.showNewTask = false
			this.store.showNewTaskModal = false
			this.createDm = false
			this.enterGroupInvite = false
		},

		postFromIframe(msg) {
			postFromIframe(msg)
		},

		chatLoaded() {
			if (this.store.config.wrapper) {
				let data = { type: 'chatLoaded' }
				this.postFromIframe(data)
			}
		},

		async getUnreadAnnouncements() {
			if (this.allUnreadAnnouncementsLoaded) return

			try {
				await fetchAllUnreadAnnouncements()
				this.allUnreadAnnouncementsLoaded = true
				this.unreadAnnouncementsError = 0
			} catch (e) {
				this.showEmptyState = true
				this.unreadAnnouncementsError += 1
			}

			// var post_data = {
			// 	query: 'UNREAD',
			// }

			// if (this.lastUnreadAnnouncementId) {
			// 	post_data.latest = this.lastUnreadAnnouncementId
			// }

			// post('/api/v2/announcement.list', post_data)
			// 	.then(({ data }) => {
			// 		if (data.announcements.length > 0) {
			// 			this.store.unreadAnnouncements.push(...data.announcements)
			// 			this.lastUnreadAnnouncementId = data.announcements.pop().id

			// 			if (data.hasmore) {
			// 				this.getUnreadAnnouncements()
			// 			}
			// 		} else {
			// 			this.allUnreadAnnouncementsLoaded = true
			// 		}
			// 	})
			// 	.catch((error) => {
			// 		this.showEmptyState = true
			// 		console.error(error)
			// 	})
		},

		logout() {
			EventBus.$emit('logoutUser')
		},

		listenForNotifications() {
			const { user } = this.store.auth
			const pushtoken = getPushToken()
			if (!this.notificationsEnabled && !this.store.config.wrapper) return
			// Subscribe to the push channel
			const name = `private-push.${user.id}.${pushtoken}`
			const channel = this.$pusher.subscribe(name)
			channel.unbind()
			bindChannelEventWithChunking(
				channel,
				'App\\Events\\PushNotification',
				this.pushNotificationHandler
			)
			channel.bind('pusher:subscription_error', () => {
				console.debug('[PUSHER] retrying to subscribe to:', name)
				setTimeout(this.listenForNotifications, 5000)
			})
		},

		listenForOrgEvents() {
			const name = `private-organization.${this.store.auth.user.organizations[0].id}`
			const channel = this.$pusher.subscribe(name)
			channel.unbind()
		},

		enableNotifications() {
			Notification.requestPermission((permission) => {
				this.store.askForNotificationPermission = false
				this.store.notificationPermission = permission
				if (permission === 'granted') {
					this.notificationsEnabled = true
					storage.remove('hideNotificationPrompt')
					sStorage.remove('hideNotificationPrompt')

					if (!getPushToken()) {
						enablePushNotifications()
					} else {
						this.listenForNotifications()
					}
				}
			})
		},

		async loadUserInfo() {
			try {
				const { user } = await fetchMyUserInfo(true)
				this.showSearchBar = !!user.organizations[0].search_enabled
			} catch (error) {
				console.error(error)
				if (!error.response?.data) return
				if (error.response?.status == 401) {
					this.logout()
					return
				}

				if (error.response.data.error === 'UNAUTHORIZED') {
					EventBus.$emit('logoutUser')
				}
			}
		},

		async loadBubbleList() {
			let timeoutId
			try {
				const log = timedLogger('bubble.list')
				log('starting...')
				const BUBBLE_LIST_TIMEOUT_MS = 30000
				const timeout = new Promise((_, reject) => {
					timeoutId = setTimeout(
						() => reject(new Error('loadBubbleList timeout')),
						BUBBLE_LIST_TIMEOUT_MS
					)
				})
				const [groupMembs, dmMembs, threadMembs] = await Promise.race([
					Promise.all([
						// fetch group memberships
						fetchMyMemberships({
							'filter[chat.is_dm]': 0,
							'filter[hidden]': 0,
							include: 'chat,stats,chat.meeting,chat.category.user_category',
						}),
						// fetch dm memberships
						fetchMyMemberships({
							'filter[chat.is_dm]': 1,
							'filter[hidden]': 0,
							include: 'chat,stats,chat.memberships.user,chat.meeting',
						}),
						// fetch thread memberships (threads are excluded by default)
						fetchMyMemberships({
							'filter[chat.is_thread]': 1,
							'filter[hidden]': 0,
							include: 'chat,stats',
						}),
					]),
					timeout,
				])
				log('done')
				log.print()

				const memberships = [...groupMembs, ...dmMembs, ...threadMembs]
				if (memberships.length === 0) {
					this.hasBubbles = false
					this.bubblesLoaded = true
					this.$router.push('/group/none')
					diffChatsInCache([])
					return
				}
				this.hasBubbles = true

				store.bubbleListComplete = true
				EventBus.$emit('bubbleListComplete')

				if (this.externalGroup) {
					redirectToRecentsOrChat(this.externalGroup)
				} else if (
					(store.currentChatId && !Object.keys(getCurrentChat()).length) ||
					!this.$route.name ||
					this.$route.name === 'home'
				) {
					redirectToLastActiveGroup()
				}
				const chats = memberships.map((m) => m.chat)
				diffChatsInCache(chats)
				this.bubbleListError = 0
			} catch (e) {
				if (isCanceled(e)) return
				if (e?.message === 'loadBubbleList timeout') {
					console.warn('[dormancy] loadBubbleList timed out after 30s')
				}
				console.error(e)
				this.bubbleListError += 1
			} finally {
				clearTimeout(timeoutId)
			}
		},

		async fetchOrgPresence() {
			try {
				const { data } = get('/api/clients/users/presence')
				data?.data?.forEach(({ user_id, ...presence }) => {
					updateUserPresence(user_id, presence)
				})
				this.orgPresenceError = 0
			} catch (e) {
				if (isCanceled(e)) return
				console.error(e)
				this.orgPresenceError += 1
			}
		},

		bubbleAddedHandler(data) {
			this.loadBubbleList()
		},

		async bubbleChangedHandler(data) {
			const chatId = data.bubble.id

			// Server now sends the user's per-membership is_hidden flag. If the chat
			// is hidden and not currently open, we don't need fresh info — skip the
			// network round-trip.
			if (data.bubble.is_hidden && chatId !== this.store.currentChatId) {
				return
			}

			const oldChat = getChat(chatId)
			const oldChannelcode = oldChat?.channelcode

			await this.loadChatInfo(chatId)

			const newChat = getChat(chatId)
			if (
				oldChannelcode &&
				newChat?.channelcode &&
				oldChannelcode !== newChat.channelcode
			) {
				// Channel code rotated — propagate to all threads and resubscribe
				const threads = getThreadsForChat(chatId)
				const activePreviewIds = getActiveThreadPreviewIds()
				for (const thread of threads) {
					updateChat(thread.id, {
						channelcode: newChat.channelcode,
					})
					if (activePreviewIds.includes(thread.id)) {
						resubscribeThreadPreview(thread.id)
					}
				}
			}
		},

		bubbleRemovedHandler(data) {
			deleteChat(data.bubble.id)
			EventBus.$emit('chat-removed', data.bubble.id)
			this.$nextTick(function () {
				this.loadBubbleList()
			})
		},

		bubbleStatsHandler({ stats }) {
			setStats(stats)
		},

		categoryUpdatedHandler({ category }) {
			setCategory(category)
		},

		userUpdatedHandler({ user }) {
			setUser(user)

			if (user.id === this.store.auth.user.id) {
				const { mute, muteuntil } = user
				Object.assign(this.store.auth.user, { mute, muteuntil })
				this.store.auth.setUser(this.store.auth.user, this.store.userKey)
			}
		},

		userPresenceHandler({ user_id, ...data }) {
			updateUserPresence(user_id, data)
		},

		membershipUpdatedHandler(data) {
			setMembership(data.membership)
		},

		organizationUpdatedHandler(data) {
			if (data.organization.id === this.store.auth.user.organizations[0].id) {
				this.store.auth.user.organizations[0] = data.organization
				this.store.auth.setUser(this.store.auth.user, this.store.userKey)
				EventBus.$emit('OrgUpdated')
			}
		},

		async loadChatInfo(chatId) {
			try {
				await fetchMyMembershipForChat(chatId)
				this.chatInfoError = 0
			} catch (e) {
				if (isCanceled(e)) return
				console.error(e)
				this.chatInfoError += 1
			}
		},

		loadTaskList(oldest) {
			if (!this.store.auth.user) {
				return
			}

			let post_data = {
				organization_id: this.store.auth.user.organizations[0].id,
				completed: false,
			}

			if (oldest) {
				post_data.latest = oldest
			}

			post('/api/v1/task.list', post_data)
				.then(({ data }) => {
					if (oldest) {
						this.store.tasks.open = this.store.tasks.open.concat(data.tasks)
					} else {
						this.store.tasks.open = data.tasks
					}

					this.$nextTick(function () {
						if (data.hasmore) {
							let oldest_task = data.tasks.reduce(function (prev, curr) {
								return prev.id < curr.id ? prev : curr
							}).id

							this.loadTaskList(oldest_task)
						}

						if (!data.hasmore) {
							this.store.updateTaskStats()
						}
					})
					this.taskListError = 0
				})
				.catch((error) => {
					if (isCanceled(error)) return
					console.error(error)
					this.taskListError += 1
				})
		},

		taskAddedHandler(data) {
			//wrap this in a timeout to make sure my model has updated before any events are fired.
			setTimeout(() => {
				let task = this.store.tasks.open.find((x) => x.id == data.task.id)
				//check in open tasks first
				if (task) {
					let position = this.store.tasks.open.findIndex(
						(obj) => obj.id == task.id
					)
					this.store.tasks.open[position] = task
					this.store.updateTaskStats()
				} else {
					//check in completed tasks
					let task = this.store.tasks.completed.find(
						(x) => x.id == data.task.id
					)
					if (task) {
						let position = this.store.tasks.completed.findIndex(
							(obj) => obj.id == task.id
						)
						this.store.tasks.completed[position] = task
						this.store.updateTaskStats()
					} else {
						this.loadTaskList()
					}
				}
			}, 5000)
		},

		taskUpdatedHandler({ task }) {
			getMessageIdsForTaskId(task.id).forEach((messageId) => {
				updateMessage(messageId, { task })
			})

			const openIndex = store.tasks.open.findIndex((t) => t.id === task.id)
			if (openIndex > -1 && !task.completed) {
				// task was previously open status and still is so just update it
				store.tasks.open = store.tasks.open.map((t) =>
					t.id === task.id ? { ...t, ...task } : t
				)
				this.store.updateTaskStats()
				return
			}
			const completedIndex = store.tasks.completed.findIndex(
				(t) => t.id === task.id
			)
			if (completedIndex > -1 && task.completed) {
				// task was previously completed status and still is so just update it
				store.tasks.open = store.tasks.open.map((t) =>
					t.id === task.id ? { ...t, ...task } : t
				)
				this.store.updateTaskStats()
				return
			}
			// task has changed status (or was previously unknown)
			// so we fallback to re-fetching the full list
			this.loadTaskList()
		},

		taskRemovedHandler(data) {
			getMessageIdsForTaskId(data.task.id).forEach((messageId) => {
				updateMessage(messageId, { task: data.task })
			})

			setTimeout(() => {
				let task = this.store.tasks.open.find((x) => x.id == data.task.id)
				//check in open tasks first
				if (task) {
					this.store.tasks.open = this.store.tasks.open.filter(
						(task) => task.id !== data.task.id
					)
					this.store.updateTaskStats()
				} else {
					//check in completed tasks
					let task = this.store.tasks.completed.find(
						(x) => x.id == data.task.id
					)
					if (task) {
						this.store.tasks.completed = this.store.tasks.completed.filter(
							(task) => task.id !== data.task.id
						)
						this.store.updateTaskStats()
					}
				}
			}, 2000)
		},

		uploadCompleteHandler({ media }) {
			if (
				(media.type === 'announcement' || media.type === 'task') &&
				media.mediatype === 'VIDEO'
			) {
				this.store.uploadedMedia = media
				this.store.uploadCompleted = true
				return
			}

			const message = getMessage(media.uuid)
			if (message && message.sendState === 'sending') {
				this.sendMessage(message, media)
			}
		},

		sendMessage(message_data, media_data) {
			let post_data = {
				bubble_id: message_data.bubble_id,
				message: message_data.message.trim(),
				uuid: message_data.uuid,
				messagemedia: [media_data],
			}

			if (message_data.parentmessage_id) {
				post_data.parentmessage_id = message_data.parentmessage_id
			}

			post('/api/v1/message.create', post_data)
				.then(({ data }) => {
					deleteMessage(data.message.uuid)
					setMessage(data.message)
					deleteUpload(data.message.uuid)
				})
				.catch((error) => {
					console.error(error)
				})
		},

		pushNotificationHandler(data) {
			if (data.type === 'message' || data.type === 'poll') {
				this.store.dbPut('messages', data.message)
			}

			if (
				this.store.appVisible === true &&
				data.bubble_id === this.store.currentChatId
			) {
				// app is active, but we're in the right bubble already
				return
			}

			const notification = pushNotification(data)
			if (notification) {
				this.postFromIframe({ type: 'newMessage', messageData: data })
				notification.onclick = () => {
					if (window.todesktop) {
						window.todesktop.window.focus()
					} else {
						parent.focus()
						window.focus() //just in case, older browsers
					}

					if (
						data.type === 'reaction' ||
						data?.message?.user_edited_at ||
						data.type === 'poll'
					) {
						this.$router.push(
							`/recents/${data.bubble_id}?centered=${data.message_id}`
						)
					} else if (data.bubble_id) {
						this.$router.push(`/recents/${data.bubble_id}`)
					}
				}
				storeNotification(data.bubble_id, notification)
			}
		},

		updateTabBadge() {
			if (isSafari) {
				setDocTitleFromCurrentRoute()
			}
			// only update badge count if count has changed (#847)
			if (this.currentTabBadgeCount !== this.allExternalUnreads) {
				window.favicon.badge(
					this.allExternalUnreads >= MAX_UNREADS
						? UNREAD_LABEL
						: this.allExternalUnreads
				)
				this.currentTabBadgeCount = this.allExternalUnreads
				if (window.todesktop && store.getActiveUsers().length === 1) {
					window.todesktop.app.setBadgeCount(this.allExternalUnreads)
				}
			}
		},

		title(bubble_title) {
			if (length(bubble_title) > 22) {
				if (bubble_title === bubble_title.toUpperCase()) {
					return substr(bubble_title, 0, 12) + '...'
				} else {
					return substr(bubble_title, 0, 22) + '...'
				}
			} else {
				return bubble_title
			}
		},

		groupSettings() {
			// this.showGroupSettings = true
			this.$router.push({ name: 'groupoptions', params: this.$route.params })
		},

		goToAnnouncements() {
			this.unloadChat()
			this.$router.push('/announcements')
			this.hamburgerActive = false
			EventBus.$emit('announcementsButtonClicked')
		},

		goToTasks() {
			if (!this.store.config.wrapper) {
				this.unloadChat()
				this.$router.push('/tasks')
			} else {
				this.openPopout(window.location.origin + '/tasks')
			}
		},

		createNewAnnouncement() {
			if (this.$route.path == '/announcements') {
				EventBus.$emit('createNewAnnouncement')
			} else {
				this.unloadChat()
				this.$router.push('/announcements?create=1')
				this.hamburgerActive = false
			}
		},

		unloadChat() {
			// if we're navigating away from our chat, we should unload it so we get the proper notifications, etc.
			let messageList = document.querySelector('div.message-list')
			if (messageList) {
				this.$refs.routerview.unload()
			}
		},

		scrollToBottom() {
			this.$refs.routerview.scrollToBottom()
		},

		subToPresence() {
			subscribeToPresence()
		},

		unSubToPresence() {
			unsubscribeToPresence()
		},

		devicePing() {
			this.checkMediaTokenTTL()

			post('/api/v1/device.ping').catch((error) => {
				console.error(error)
			})
			this.store.lastRefresh = moment().utc().format('YYYY-MM-DD HH:mm:ss')
			setTimeout(this.devicePing, 300000)
		},

		newTask({ chat_id, chat_title, chat_type } = {}) {
			this.showGroupSettings = false
			this.taskData = {
				taskmedia: [],
				user: this.store.auth.user,
				parent: 'Home.vue',
			}
			if (chat_id) {
				if (chat_type === 'dm') {
					this.taskData.assignedUser = chat_id
				} else {
					this.taskData.assignedGroup = chat_id
				}
				this.taskData.assignedTitle = chat_title
				this.taskData.isAssigned = true
				this.taskData.showAssignTo = false
			}
			this.showNewTask = true
			this.store.showNewTaskModal = true
		},

		createDirectMessage() {
			this.showGroupSettings = false
			this.createDm = true
		},

		openGroupModal(users) {
			this.createDm = false
			this.showGroupSettings = false
			this.createGroup = true
			this.createGroupUsers = users
		},

		createNewTask() {
			this.newTask()
		},

		enterCode() {
			this.showGroupSettings = false
			this.enterGroupInvite = true
		},

		handleError(value) {
			this.errorText = value
			this.showError = true
		},

		setToday() {
			this.store.today = moment().utc().format('YYYY-MM-DD HH:mm:ss')
			window.setTimeout(this.setToday, 15000)
		},

		closeAllTaskScreens() {
			this.showNewTask = false
			this.store.showNewTaskModal = false
		},

		initPusherUser() {
			// One shared timer slot: any re-entry (dormancy-refresh,
			// reloadPusherInstance, the null-channel retry, or the
			// subscription_error retry) cancels the pending attempt
			// before scheduling a new one — prevents parallel retry chains.
			clearTimeout(this._initPusherTimer)
			this._initPusherTimer = null

			const userChannel = subscribeToPrivateUser()
			if (!userChannel) {
				this._initPusherTimer = setTimeout(this.initPusherUser, 5000)
				return
			}

			//unbind any orphaned events
			userChannel.unbind()

			userChannel.bind('pusher:subscription_error', () => {
				this._initPusherTimer = setTimeout(this.initPusherUser, 5000)
			})

			bindAppEvents({
				BubbleAdded: this.bubbleAddedHandler.bind(this),
				BubbleChanged: this.bubbleChangedHandler.bind(this),
				BubbleRemoved: this.bubbleRemovedHandler.bind(this),
				BubbleStats: this.bubbleStatsHandler.bind(this),
				CategoryUpdated: this.categoryUpdatedHandler.bind(this),
				TaskAdded: this.taskAddedHandler.bind(this),
				TaskUpdated: this.taskUpdatedHandler.bind(this),
				TaskRemoved: this.taskRemovedHandler.bind(this),
				UserUpdated: this.userUpdatedHandler.bind(this),
				UploadComplete: this.uploadCompleteHandler.bind(this),
				UserPresence: this.userPresenceHandler.bind(this),
				MembershipUpdated: this.membershipUpdatedHandler.bind(this),
				AnnouncementAdded: this.announcementAddedHandler.bind(this),
				AnnouncementUpdated: this.announcementUpdatedHandler.bind(this),
				AnnouncementRemoved: this.announcementRemovedHandler.bind(this),
				OrganizationUpdated: this.organizationUpdatedHandler.bind(this),
			})
		},

		openPopout(popout_url) {
			openPopout(popout_url)
		},

		closeWrapper() {
			if (this.store.config.wrapper) {
				let data = { type: 'closeWrapper' }
				this.postFromIframe(data)
			}
		},

		async externalGroupRedirect(group_id) {
			try {
				const myId = getMyUserId()
				const { data } = await post('/api/lti/grouplookup', {
					group_id,
					organization_id: store.auth.user.organizations[0].id,
					user_id: myId,
				})
				if (!data.ok) throw new Error(data.error)
				redirectToRecentsOrChat(data.bubble_id)
			} catch (e) {
				console.error(e)
			}
		},

		async externalCourseRedirect(course_id) {
			try {
				const myId = getMyUserId()
				const { data } = await post('/api/lti/courselookup', {
					course_id,
					organization_id: store.auth.user.organizations[0].id,
					user_id: myId,
				})
				if (!data.ok) throw new Error(data.error)
				this.externalGroup = data.bubble_id
				redirectToRecentsOrChat(this.externalGroup)
			} catch (e) {
				console.error(e)
			}
		},

		announcementAddedHandler({ announcement }) {
			setAnnouncement(announcement)
		},

		announcementRemovedHandler({ announcement_id }) {
			deleteAnnouncement(announcement_id)
		},

		announcementUpdatedHandler({ announcement }) {
			setAnnouncement(announcement)
		},

		totalUnread() {
			return getRecentChats().reduce((sum, c) => sum + c.unread, 0)
		},

		totalUnreadMentions() {
			return getRecentChats().reduce(
				(sum, c) => sum + getEffectiveMentions(c),
				0
			)
		},

		preventDefaultDrop(e) {
			e.preventDefault()
		},

		setUserAsActive() {
			clearTimeout(this.presenceTimeout)
			this.subToPresence()

			this.presenceTimeout = setTimeout(() => {
				this.unSubToPresence()
				this.presenceTimeout = null
			}, FIFTEEN_MINS)
		},
	},
}
</script>

<style lang="scss">
@import 'src/sass/variables';

@media screen and (max-width: 800px) {
	.announcements-label {
		right: 15px !important;
	}

	.modal-box-md {
		min-width: 100vw !important;
		margin-top: unset !important;
		margin-left: unset !important;
		border-radius: unset !important;
		top: 0 !important;
		left: 0 !important;
	}

	.detail-modal {
		width: unset !important;
		margin-left: 0px !important;
	}

	.detail-modal-message {
		width: unset !important;
	}

	.full-sidebar .side-menu-main {
		z-index: 9999 !important;
		background-color: #fff !important;
		.non-mentions {
			background-color: var(--neutral-100) !important;
		}
	}

	.group-title {
		margin-left: 68px !important;
	}
	.settings-gear {
		display: flex !important;
	}
	.message-control {
		.group-title {
			padding-left: 0;
		}
	}
}

.cloud-loader {
	@include absolute-cover;
	width: 100vw;
	height: 100vh;
}

.create-options-card {
	width: 280px;
	background-color: #ffffff;
	-webkit-transition: all 0.3s ease-in-out;
	-moz-transition: all 0.3s ease-in-out;
	-ms-transition: all 0.3s ease-in-out;
	-o-transition: all 0.3s ease-in-out;
	transition: all 0.3s ease-in-out;
	overflow: hidden;
	max-height: 0;
	position: absolute;
	left: 0;
	bottom: 0px;
	padding-left: 30px;
}
.create-options-card > div:nth-of-type(1) {
	font-family: Avenir-Heavy;
	font-size: 16px;
	margin-top: 40px;
	color: #000000;
}
.create-options-card div {
	margin-bottom: 29px;
}
.create-options-card > div:nth-of-type(2) img {
	height: 21px;
	width: 22px;
	vertical-align: middle;
	margin-top: -2.5px;
	margin-right: 15px;
}
.create-options-card > div:nth-of-type(3) img {
	height: 20px;
	width: 22px;
	vertical-align: middle;
	margin-top: -2.5px;
	margin-right: 15px;
}
.create-options-card > div:nth-of-type(4) img {
	height: 20px;
	width: 22px;
	vertical-align: middle;
	margin-top: -3.5px;
	margin-right: 15px;
}
.create-options-card > div:nth-of-type(5) img {
	height: 19px;
	width: 23px;
	vertical-align: middle;
	margin-top: -3.5px;
	margin-right: 15px;
}
.create-options-card > div:nth-of-type(6) img {
	height: 15px;
	width: 23px;
	vertical-align: middle;
	margin-top: -3.5px;
	margin-right: 15px;
}
.create-options-card > div:nth-of-type(6) {
	margin-bottom: 95px;
}

.create-options-card {
	.join-group {
		display: flex;
		align-items: center;
		.pronto-icon {
			font-size: 20px;
			margin-right: 22px;
		}
	}
}

.create-options-card .pointer {
	outline: 0;
}

.create-options-card-expanded {
	max-height: 400px;
}

.announcements-active {
	background-color: var(--teal-300);
}

.announcements-active .announcements-label,
.announcements-active .announcements-state {
	color: #fff;
}

.announcements-label {
	position: absolute;
	top: 5px;
	left: 50px;
}

.actions-container {
	position: relative;
	height: 80px;
	display: flex;
}

.no-actions-condensed {
	height: 20px;
}

#task-alert-icon {
	top: 6.5px;
	right: 22px;
}

.tasks-state,
.announcements-state {
	font-family: Avenir-Roman;
	font-size: 10px;
	color: #a1a2a6;
}

.tasks-state {
	position: absolute;
	right: 3px;
	bottom: 10px;
	min-width: 71px;
	text-align: left;
}

.announcements-state {
	position: absolute;
	left: 50px;
	bottom: 10px;
	padding-left: 3px;
	padding-right: 3px;
}

.main-settings:focus {
	outline: 0;
}

.detail-modal-header {
	height: 60px;
	display: block;
	border-bottom: 1px solid #f0f2f3;
}

.detail-modal-header-text {
	font-family: Avenir;
	font-size: 20px;
	font-weight: 900;
	line-height: 3;
	letter-spacing: -0.07px;
	text-align: center;
	color: #4f5053;
}

.task-state-due {
	left: 55px;
	right: auto;
	padding-left: 3px;
	padding-right: 3px;
}

.error-wrapper {
	position: fixed;
	z-index: 101;
	top: 0;
	left: 16px;
	right: 16px;
}

.error-wrapper-embedded {
	position: fixed;
	z-index: 4;
	top: 0;
	left: 20px;
	right: 20px;
}

.meetings-error-modal {
	.modal-body {
		padding: 24px 110px 24px 110px;
		@include flex-column-center;
		.pronto-icon-halo {
			color: var(--red-200);
			font-size: 55px;
			margin-bottom: 24px;
		}
		.meetings-error-message {
			font-size: 18px;
			margin-bottom: 16px;
			text-align: center;
			&:last-child {
				margin-bottom: 0;
			}
		}
	}
}

.loading-too-long-container {
	@include flex-column(16px);
	align-items: center;
	height: 0;
	overflow: visible;
	> * {
		flex-shrink: 0;
	}
}

.search-icon {
	position: absolute;
	right: 100px;
	top: 20px;
	outline: 0;
}

.header {
	grid-area: header;
}

.relative {
	position: relative;
}

.group-title-text,
.hidden-group-icon {
	display: inline-block;
}

.group-icon {
	font-size: 32px;
}

.hidden-group-icon {
	width: 16px;
	height: 14px;
	margin-left: 6px;
	vertical-align: middle;
}

.load-spinner {
	left: 50%;
	transform: translateX(-50%);
}

.side-menu-content {
	padding-bottom: 70px;
}
.side-menu-content ul > li > a,
.side-menu-content ul > li > button {
	font-family: Avenir-Roman;
	font-size: 16px;
	color: #4b4b4c;
	display: block;
	margin-left: 13px;
	height: 42px;
	position: relative;
	width: 267px;
}
.side-menu-content ul > li > a:hover,
.side-menu-content ul > li > button:hover {
	text-decoration: none;
	cursor: pointer;
	background-color: #e8ebec;
}

.side-menu-content ul > li > a:hover :not(.router-link-active) .groupListTitle,
.side-menu-content
	ul
	> li
	> button:hover
	:not(.router-link-active)
	.groupListTitle {
	background-color: #e8ebec;
}
.side-menu-content ul > li:active {
	background: #f4f8fb !important;
	color: #5fbeaa !important;
}

@media screen and (max-width: 800px) {
	.side-menu-content {
		margin-top: 30px;
	}
}
</style>
